import { MediaContextProvider } from "@jewlr/storybook/core"
import loadable from "@loadable/component"
import { SSRProvider } from "@react-aria/ssr"
import { Hydrate, QueryClientProvider } from "@tanstack/react-query"
import { Suspense, useEffect, useRef, useState } from "react"
import { HelmetProvider } from "react-helmet-async"
import { HistoryRouter as Router } from "redux-first-history/rr6"
import { ThemeProvider } from "styled-components"

import { history, queryClient } from "app-store-v2"
import SimpleDeferAfterLoadWrapper from "components/common/simple-defer-load-wrapper"
import ResetFocus from "components/reset-focus"
import { ScrollTrackerProvider } from "components/scroll-tracker"
import { MediaContextProvider as UIMediaContextProvider } from "components/ui/media"
import { IS_JEWLR, retry } from "helpers/application"
import { initialLazySizes } from "initializers/utils"
import orgTheme from "styles/org-theme"

import AppReduxProvider from "./AppReduxProvider"

const ReactQueryDevtools = loadable(() =>
  retry(() =>
    import("@tanstack/react-query-devtools").then((d) => ({
      default: d.ReactQueryDevtools,
    }))
  )
)
const FullLoading = loadable(() =>
  retry(() => import("components/full-loading"))
)
const AuthenticateModal = loadable(() =>
  retry(() => import("areas/authenticate"))
)
const ForgotPasswordModal = loadable(() =>
  retry(() => import("areas/authenticate/forgot-password"))
)
const LogoutModal = loadable(() =>
  retry(() => import("areas/authenticate/logout-modal"))
)
const ShareProductModal = loadable(() =>
  retry(() => import("areas/header/modals/share-products"))
)
const GeoModal = loadable(() => retry(() => import("app/locale-dialog")))

const ReferralRegisterModal = loadable(() =>
  retry(() => import("areas/authenticate/referral-register"))
)
const OptOutModal = loadable(() =>
  retry(() => import("components/opt-out-modal"))
)
const ConsentBanner = loadable(() => retry(() => import("app/consent-bnr")))
const ConsentModal = loadable(() => retry(() => import("app/consent-modal")))
// const BestSaleModal = loadable(() => retry(() => import("app/best-sale-modal")))

const AppModals = () => {
  return (
    <SimpleDeferAfterLoadWrapper>
      <FullLoading />
      {IS_JEWLR() && <AuthenticateModal />}
      {IS_JEWLR() && <ForgotPasswordModal />}
      <ShareProductModal />
      {IS_JEWLR() && <LogoutModal />}
      {IS_JEWLR() && <ReferralRegisterModal />}
      <GeoModal />
      {IS_JEWLR() && <OptOutModal />}
      {IS_JEWLR() && <ConsentBanner />}
      {IS_JEWLR() && <ConsentModal />}
      {/* {IS_JEWLR() && <BestSaleModal />} */}
    </SimpleDeferAfterLoadWrapper>
  )
}

export const withClientProviders = (Component) => {
  const InternalWithClientProviders = (props) => {
    const navTopRef = useRef()
    const skippedRef = useRef()
    const [showDevTools, setShowDevTools] = useState(false)

    const dehydratedState = window.__REACT_QUERY_STATE__

    useEffect(() => {
      initialLazySizes()

      window.toggleQueryDevTools = () => setShowDevTools(!showDevTools)

      let timer = setTimeout(() => {
        window.location.reload()
      }, 3600000) // 1 hr

      return history.listen(() => {
        clearTimeout(timer)
        timer = setTimeout(() => {
          window.location.reload()
        }, 3600000) // 1 hr
      })
    }, [])

    const handleSkipToMainContent = (e) => {
      skippedRef.current?.focus()
      e.preventDefault()
      e.stopPropagation()
    }

    return (
      <QueryClientProvider client={queryClient}>
        <Hydrate state={dehydratedState}>
          <SSRProvider>
            <HelmetProvider>
              <ThemeProvider theme={orgTheme()}>
                <AppReduxProvider {...props}>
                  <MediaContextProvider>
                    <UIMediaContextProvider>
                      <Router basename={sl.basename} history={history}>
                        <ScrollTrackerProvider>
                          <ResetFocus navTopRef={navTopRef} />
                          <Component
                            {...props}
                            handleSkipToMainContent={handleSkipToMainContent}
                            navTopRef={navTopRef}
                            skippedRef={skippedRef}
                          />
                          <AppModals />
                          {showDevTools && (
                            <Suspense fallback={null}>
                              <ReactQueryDevtools />
                            </Suspense>
                          )}
                        </ScrollTrackerProvider>
                      </Router>
                    </UIMediaContextProvider>
                  </MediaContextProvider>
                </AppReduxProvider>
              </ThemeProvider>
            </HelmetProvider>
          </SSRProvider>
        </Hydrate>
      </QueryClientProvider>
    )
  }

  return InternalWithClientProviders
}
